import React from "react"
import { FaSpinner } from "react-icons/fa"

interface PlusOnesPopupProps {
  isOpen: boolean
  isLoading: boolean
  plusOneCount: number
  plusOneAllowedNumber: number[]
  isEditMode: boolean
  showAlert: boolean
  onClose: () => void
  onSubmit: (count: number) => void
  onChange: (count: number) => void
}

const PlusOnesPopup: React.FC<PlusOnesPopupProps> = ({
  isOpen,
  isLoading,
  plusOneCount,
  plusOneAllowedNumber,
  isEditMode,
  showAlert,
  onClose,
  onSubmit,
  onChange,
}) => {
  const [shouldShowAlert, setShouldShowAlert] = React.useState(showAlert)
  React.useEffect(() => {
    setShouldShowAlert(showAlert)
  }, [showAlert])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleSubmit = () => {
    onSubmit(plusOneCount)
  }

  const min = plusOneAllowedNumber[0] ?? 0
  const max = plusOneAllowedNumber[1] ?? 1
  if (shouldShowAlert) {
    return (
      <div
        className="fixed max-h-screen  inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={handleOverlayClick}
      >
        <div className="bg-white/30 backdrop-blur-md rounded-2xl p-8 w-[90%] max-w-md shadow-lg">
          <h2 className="text-6xl font-semibold text-white text-center mb-4">
            {`⚠️`}
          </h2>
          <h2 className="text-2xl font-semibold text-white text-center mb-8">
            {`Important notice`}
          </h2>

          <div className="mb-6">
            <label className="text-[#C2C2C2] text-center text-md mb-2 block">
              If you add or edit your +1s, your status will return to “Waiting
              for Approval”, and the host will need to approve you again.
            </label>
          </div>

          <button
            onClick={() => {
              setShouldShowAlert(false)
            }}
            className="w-full bg-white/15 backdrop-blur-md text-white py-3 rounded-[30px] overflow-hidden mb-3 flex items-center justify-center hover:bg-white/40 transition-all"
          >
            Continue
          </button>

          <button
            onClick={onClose}
            className="w-full bg-transparent text-white transition-all"
          >
            Close
          </button>
        </div>
      </div>
    )
  }
  return (
    <div
      className="fixed max-h-[100%] inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={handleOverlayClick}
    >
      <div className="bg-white/30 backdrop-blur-md rounded-2xl p-8 w-[90%] max-w-md shadow-lg">
        <h2 className="text-2xl font-semibold text-white text-center mb-8">
          {isEditMode ? "Edit +1s" : "Add +1s"}
        </h2>

        <div className="mb-6">
          <label className="text-[#C2C2C2] text-left text-sm mb-2 block">
            {isEditMode ? "Edit a +1" : "Add a +1"}
          </label>

          <select
            className="w-full p-3 rounded-lg bg-white/30 backdrop-blur-md text-white border-none focus:outline-none"
            value={plusOneCount}
            onChange={(e) => onChange(Number(e.target.value))}
          >
            {Array.from({ length: max - min + 1 }, (_, i) => (
              <option key={i} value={min + i}>
                {min + i}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full bg-white/15 backdrop-blur-md text-white py-3 rounded-[30px] overflow-hidden mb-3 flex items-center justify-center hover:bg-white/40 transition-all"
        >
          {isLoading ? (
            <FaSpinner className="animate-spin" />
          ) : isEditMode ? (
            "Update"
          ) : (
            "Add"
          )}
        </button>

        <button
          onClick={onClose}
          className="w-full bg-transparent text-white transition-all"
        >
          Close
        </button>
      </div>
    </div>
  )
}

export default PlusOnesPopup
